import type { FC, MouseEvent, MutableRefObject } from 'react'
import type { PartialUser, User } from '@/api'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import Tooltip from '@mui/material/Tooltip'
import { usePopover } from '@/hooks/use-popover'
import useTranslate from '@/hooks/use-translate'
import { AccountPopover } from '../account-button/account-popover'
import { SIDE_NAV_ICON_COLUMN_PADDING_LEFT_PX } from './side-nav-item'
import {
  SIDE_NAV_ITEM_FONT_SIZE,
  SIDE_NAV_POPOVER_OFFSET_PX,
  sideNavPopoverBackdropSx,
  sideNavPopoverPaperShadow,
} from './side-nav-layout-dimensions'
import { densityPx } from '@/theme/base/density'
import PersonIcon from '@mui/icons-material/Person'
import Typography from '@mui/material/Typography'

// const ACCOUNT_MENU_TRIGGER_SIZE = 40

type SideNavAccountFooterProps = {
  user: Partial<User> | null
  accountMenuOpenRef: MutableRefObject<boolean>
  isFolded: boolean
  showText: boolean
}

export const SideNavAccountFooter: FC<SideNavAccountFooterProps> = ({
  user,
  accountMenuOpenRef,
  isFolded,
  showText,
}) => {
  const { t: tNav } = useTranslate('nav')
  const popover = usePopover<HTMLButtonElement>()

  if (!user?.email) {
    return null
  }

  const displayName = `${user.profile?.firstName ?? ''} ${
    user.profile?.lastName ?? ''
  }`.trim()

  // const initialsSource = displayName || user.email || ''

  const openMenu = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    accountMenuOpenRef.current = true
    popover.handleOpen()
  }

  const handlePopoverClose = () => {
    accountMenuOpenRef.current = false
    popover.handleClose()
  }

  return (
    <>
      <Tooltip
        disableHoverListener={!isFolded}
        enterDelay={300}
        placement="right"
        title={tNav('accountMenuTooltip')}
      >
        <ButtonBase
          aria-haspopup="true"
          data-testid="sidenav-account-trigger"
          ref={popover.anchorRef}
          onClick={openMenu}
          sx={{
            alignItems: 'center',
            backgroundColor: '#0E293D',
            border: '1px solid',
            borderColor: 'primary.dark',
            borderRadius: 1,
            color: 'var(--nav-color)',
            display: 'flex',
            gap: isFolded ? 0 : 1,
            height: densityPx(40),
            justifyContent: 'flex-start',
            minWidth: 'unset',
            pl: `${SIDE_NAV_ICON_COLUMN_PADDING_LEFT_PX}px`,
            pr: `${densityPx(10)}px`,
            py: 0.5,
            textAlign: 'left',
            width: isFolded ? 'fit-content' : '100%',
            '&:hover': {
              backgroundColor: 'rgba(19, 98, 132, 0.7)',
            },
          }}
        >
          <Box
            component="span"
            sx={{
              alignItems: 'center',
              color: 'currentColor',
              display: 'inline-flex',
              flex: '0 0 auto',
              height: densityPx(28),
              justifyContent: 'center',
            }}
          >
            <PersonIcon fontSize="small" sx={{ color: 'currentColor' }} />
          </Box>
          {!isFolded && (
            <Typography
              sx={{
                color: 'inherit',
                flexGrow: 1,
                fontSize: SIDE_NAV_ITEM_FONT_SIZE,
                fontWeight: 600,
                lineHeight: `${densityPx(28)}px`,
                minWidth: 0,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                opacity: showText ? 1 : 0,
                pointerEvents: showText ? 'auto' : 'none',
                transform: showText
                  ? 'translateX(0)'
                  : `translateX(-${densityPx(10)}px)`,
                transition: 'opacity 0.3s ease, transform 0.3s ease',
              }}
            >
              {displayName}
            </Typography>
          )}
        </ButtonBase>
        {/* </Box> */}
      </Tooltip>
      <AccountPopover
        anchorEl={popover.anchorRef.current}
        anchorOrigin={{ horizontal: 'right', vertical: 'center' }}
        logoutAsButton
        open={popover.open}
        hideBackdrop={false}
        BackdropProps={{ sx: sideNavPopoverBackdropSx }}
        PaperProps={{
          elevation: 0,
          sx: {
            boxShadow: sideNavPopoverPaperShadow,
            marginLeft: `${SIDE_NAV_POPOVER_OFFSET_PX}px`,
            width: densityPx(230),
          },
        }}
        transformOrigin={{ horizontal: 'left', vertical: 'center' }}
        user={user as PartialUser}
        onClose={handlePopoverClose}
      />
    </>
  )
}
